import { DataGrid, GridCallbackDetails, GridColDef, GridColumnVisibilityModel, GridDensity, GridFilterModel, GridPaginationModel, GridValidRowModel } from "@mui/x-data-grid";
import { GridApiCommunity } from "@mui/x-data-grid/internals";
import { parseAsJson, useQueryState } from "nuqs";
import { ComponentProps, memo, useMemo, useRef } from "react";

import { orderSearchParamsSchema } from "@/pages/_private/dashboard/_base/pedidos";
import { cn } from "@/utils";

type DataGridBaseProps<T extends GridValidRowModel> = Omit<ComponentProps<typeof DataGrid>, "rows" | "columns"> & {
  rows: T[];
  columns: readonly GridColDef<T>[];
  $onPaginationChange?: (model: GridPaginationModel) => void;
  $onFilterChange?: (model: GridFilterModel) => void;
};

export const useDataGridBaseParams = (_defaultValues?: object) => {
  // const search: { filter: object } = useSearch({ strict: false });
  // const query = useQueryState("filter", parseAsJson(orderSearchParamsSchema).withDefault(defaultValues || search.filter));
  const query = useQueryState("filter", parseAsJson(orderSearchParamsSchema));

  return query;
};

function DataGridBaseComponent<T extends GridValidRowModel>({ rows = [], columns, $onPaginationChange, $onFilterChange, ...rest }: DataGridBaseProps<T>) {
  const dataGridRef = useRef<GridApiCommunity>(null);
  const getDefaultValues = () => {
    if (dataGridRef.current) {
      const state = dataGridRef.current.state;
      return {
        // items: state.filter.filterModel.items,
        // quickFilterValues: state.filter.filterModel.quickFilterValues,
        settings: {
          pagination: {
            pageSize: state.pagination.paginationModel.pageSize,
            page: state.pagination.paginationModel.page
          },
          density: state.density,
          columnVisibility: state.columns.columnVisibilityModel
        }
      };
    }
  };
  const [json, setJson] = useDataGridBaseParams(getDefaultValues());

  const defaultSettings = useMemo(
    () => ({
      items: json?.items || [],
      quickFilterValues: json?.quickFilterValues || [],
      settings: {
        pagination: {
          pageSize: json?.settings?.pagination?.pageSize ?? 8,
          page: json?.settings?.pagination?.page ?? 0
        },
        density: json?.settings?.density ?? "comfortable",
        columnVisibility: json?.settings?.columnVisibility || {}
      }
    }),
    [json]
  );

  const handleFilterModelChange = (model: GridFilterModel, _details: GridCallbackDetails<"filter">) => {
    $onFilterChange?.(model);
    setJson((prev) => ({
      ...prev,
      items: model.items,
      quickFilterValues: model.quickFilterValues
    }));
  };
  const handlePaginationModelChange = (model: GridPaginationModel, _details: GridCallbackDetails<"pagination">) => {
    $onPaginationChange?.(model);
    setJson((prev) => ({
      ...prev,
      settings: {
        ...prev?.settings,
        pagination: {
          ...prev?.settings?.pagination,
          pageSize: model.pageSize,
          page: model.page
        }
      }
    }));
  };
  const handleColumnVisibilityModelChange = (model: GridColumnVisibilityModel, _details: GridCallbackDetails) => {
    setJson((prev) => ({
      ...prev,
      settings: {
        ...prev?.settings,
        columnVisibility: model
      }
    }));
  };

  const handleDensityChange = (density: GridDensity) => {
    setJson((prev) => ({
      ...prev,
      settings: {
        ...prev?.settings,
        density
      }
    }));
  };

  return (
    <DataGrid
      rows={rows}
      columns={columns as GridColDef<GridValidRowModel>[]}
      filterDebounceMs={300}
      initialState={{
        pagination: {
          paginationModel: defaultSettings.settings?.pagination
        },
        filter: {
          filterModel: {
            items: defaultSettings.items,
            quickFilterValues: defaultSettings.quickFilterValues
          }
        },
        columns: {
          columnVisibilityModel: defaultSettings.settings?.columnVisibility
        },
        density: defaultSettings.settings?.density
      }}
      slotProps={{
        loadingOverlay: {
          variant: "skeleton"
        }
      }}
      onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
      onPaginationModelChange={handlePaginationModelChange}
      onFilterModelChange={handleFilterModelChange}
      onDensityChange={handleDensityChange}
      getRowClassName={({ indexRelativeToCurrentPage }) => {
        const isEven = indexRelativeToCurrentPage % 2 === 0;
        const evenStyle = cn("even-row");
        const oddStyle = cn("odd-row");

        return isEven ? evenStyle : oddStyle;
      }}
      pageSizeOptions={[5, 8, 10, 25, 50, 75, 100, { value: -1, label: "Todos" }]}
      columnVisibilityModel={defaultSettings.settings?.columnVisibility}
      paginationModel={defaultSettings.settings?.pagination}
      density={defaultSettings.settings?.density}
      filterModel={{
        items: defaultSettings.items,
        quickFilterValues: defaultSettings.quickFilterValues
      }}
      disableRowSelectionOnClick
      hideFooterSelectedRowCount
      checkboxSelection={false}
      rowSelection={false}
      hideFooter={rows.length <= (dataGridRef.current?.state.pagination.paginationModel.pageSize || 8)}
      apiRef={dataGridRef}
      showToolbar
      pagination
      {...rest}
    />
  );
}

DataGridBaseComponent.displayName = "DataGridBase";

export const DataGridBase = memo(
  DataGridBaseComponent /* , (prevProps, nextProps) => {
  return prevProps.rows === nextProps.rows && prevProps.columns === nextProps.columns;
} */
) as typeof DataGridBaseComponent;
